import { ArrowRight, ArrowUpRight } from "lucide-react";

const IMG = "/assets/fin-reco-demo.png";
const STAGES = ["Attract & Hire", "Onboard", "Serve", "Develop & Retain", "Offboard"];

/* Dark Hire-to-Retire block for context */
const DarkBlock = () => (
  <div className="rounded-xl bg-[#211307] text-[#F7F5F0] p-5 lg:p-6">
    <h4 className="font-display text-xl font-medium">Hire-to-Retire</h4>
    <p className="text-[13px] text-white/70 mt-2 mb-5 max-w-2xl leading-relaxed">Transform the complete employee lifecycle with AI agents working across every stage of the employee journey.</p>
    <div className="flex flex-wrap items-center gap-2.5">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center gap-1.5">
          <span className="flex items-center gap-2 px-1 py-2">
            <span className="font-mono2 text-[10px] text-[#E8AA68]">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-[12.5px] font-semibold">{s}</span>
          </span>
          {i < STAGES.length - 1 && <ArrowRight size={14} className="text-[#E8AA68]" />}
        </div>
      ))}
    </div>
  </div>
);

const Frame = ({ children }) => (
  <div className="relative rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)]">
    <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
      <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
      <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">fin-reco.demo.intugle.ai</span>
    </div>
    <div className="relative aspect-[16/11] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
      {children}
    </div>
  </div>
);

const Caption = ({ labelClass, titleClass, btnClass }) => (
  <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
    <div>
      <p className={`font-mono2 text-[10px] uppercase tracking-[0.22em] ${labelClass}`}>Finance Close</p>
      <p className={`text-[15px] font-semibold mt-0.5 ${titleClass}`}>Explore the live demo</p>
    </div>
    <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${btnClass}`}><ArrowUpRight size={18} strokeWidth={2.4} /></span>
  </div>
);

/* A · Warm caramel gradient */
const A = () => (
  <Frame>
    <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#7A4A28] via-[#7A4A28]/55 to-transparent" />
    <Caption labelClass="text-[#FBE0BC]" titleClass="text-white" btnClass="bg-white text-[#7A4A28]" />
  </Frame>
);

/* B · Light frosted glass bar */
const B = () => (
  <Frame>
    <div className="absolute inset-x-2.5 bottom-2.5 rounded-lg bg-white/75 backdrop-blur-md border border-white/60 shadow-lg px-4 py-3 flex items-center justify-between">
      <div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730]">Finance Close</p>
        <p className="text-[14px] font-semibold text-[#3E2818] mt-0.5">Explore the live demo</p>
      </div>
      <span className="w-8 h-8 rounded-full bg-[#211307] text-white flex items-center justify-center shrink-0"><ArrowUpRight size={16} strokeWidth={2.4} /></span>
    </div>
  </Frame>
);

/* C · Solid cream bar */
const CFrame = () => (
  <div className="relative rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)]">
    <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
      <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
      <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">fin-reco.demo.intugle.ai</span>
    </div>
    <div className="relative aspect-[16/10] overflow-hidden">
      <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
    </div>
    <div className="flex items-center justify-between bg-[#FBF2E1] px-4 py-3.5 border-t border-[#8A5730]/15">
      <div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#B87A32]">Finance Close</p>
        <p className="text-[14px] font-semibold text-[#3E2818] mt-0.5">Explore the live demo</p>
      </div>
      <span className="w-8 h-8 rounded-full bg-[#211307] text-white flex items-center justify-center shrink-0"><ArrowUpRight size={16} strokeWidth={2.4} /></span>
    </div>
  </div>
);

/* D · Gold gradient */
const D = () => (
  <Frame>
    <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#C97F1E] via-[#C97F1E]/50 to-transparent" />
    <Caption labelClass="text-white/90" titleClass="text-white" btnClass="bg-white text-[#B0670F]" />
  </Frame>
);

/* E · No band — text with shadow directly on image */
const E = () => (
  <Frame>
    <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
      <div className="[text-shadow:0_1px_12px_rgba(0,0,0,0.75)]">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#FBE0BC]">Finance Close</p>
        <p className="text-[15px] font-semibold text-white mt-0.5">Explore the live demo</p>
      </div>
      <span className="w-9 h-9 rounded-full bg-white/95 text-[#211307] flex items-center justify-center shrink-0 shadow-lg"><ArrowUpRight size={18} strokeWidth={2.4} /></span>
    </div>
  </Frame>
);

/* F · Sage gradient (palette hue) */
const F = () => (
  <Frame>
    <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#3B4A2C] via-[#3B4A2C]/55 to-transparent" />
    <Caption labelClass="text-[#D7E4C4]" titleClass="text-white" btnClass="bg-white text-[#3B4A2C]" />
  </Frame>
);

const OPTIONS = [
  { id: "a", title: "Option A · Warm Caramel Gradient", note: "Same layout, but the bottom fades to warm caramel instead of espresso — a clearly different brown from the dark block.", comp: A },
  { id: "b", title: "Option B · Frosted Glass Bar", note: "A light floating glass bar over the shot. Bright and premium — opposite tone to the dark block, so no clash.", comp: B },
  { id: "c", title: "Option C · Solid Cream Bar", note: "A separate cream-gold bar beneath the image (not overlaid). Clean, light, and clearly distinct from the dark block.", comp: CFrame },
  { id: "d", title: "Option D · Gold Gradient", note: "Bottom fades to a rich gold/amber — energetic and on-brand, visually different from the espresso block.", comp: D },
  { id: "e", title: "Option E · No Band, Text Shadow", note: "No fill at all — caption sits on the image with a soft text shadow. Lightest touch; keeps the screenshot fully visible.", comp: E },
  { id: "f", title: "Option F · Sage Gradient", note: "Bottom fades to muted sage from the palette — a different hue entirely, so it reads separate from the brown block.", comp: F },
];

export default function DemoThumbBottomOptions() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Demo Thumbnail Bottom · Options</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Different bottom treatments for the thumbnail</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">The Hire-to-Retire block stays dark (shown left for context). Each thumbnail tries a different bottom treatment so the two no longer clash. Tell me the letter.</p>

        <div className="mt-10 space-y-14">
          {OPTIONS.map((o) => {
            const Cm = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#E8AA68] mb-3">{o.title}</p>
                <div className="grid lg:grid-cols-[1fr_0.55fr] gap-6 lg:gap-10 items-start">
                  <DarkBlock />
                  <Cm />
                </div>
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
